var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getAssetPath } from "@stencil/core";
/**
 * Convenience method for using fetch but setting a time limit for the response
 * to come back.
 *
 * NOTE: 5 seconds is the TI standard timeout
 *
 * @param url Url to fetch
 * @param options Options to forward to regular fetch() method
 * @param timeout Time to wait before rejecting the promise (optional)
 */
export function fetchWithTimeout(url, options, timeout = 5000) {
    return Promise.race([
        fetch(url, options),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), timeout))
    ]);
}
/**
 * Tries to figure out a host name to use for lower environments
 */
let _envDomainForLinks = '';
export function getEnvDomainForLinks() {
    if (_envDomainForLinks === '') {
        let domainToUse = window.location.hostname;
        if (domainToUse === 'localhost') {
            domainToUse += window.location.port
                ? ':' + window.location.port
                : '';
        }
        else if (!domainToUse.match(/^(www\.)/)) {
            // eslint-disable-next-line no-useless-escape
            const env = domainToUse.match(/^([a-z0-9\.\-]+\.)?(ti\.com\.cn|tij\.co\.jp|ti\.com)/);
            domainToUse = !env
                ? 'www.ti.com'
                // eslint-disable-next-line no-useless-escape
                : env[0].match(/^(www\-)/)
                    ? env[0]
                    : 'www.' + env[2];
        }
        _envDomainForLinks = domainToUse;
    }
    return _envDomainForLinks;
}
/**
 * Cheap function for doing like a "sleep" or "timeout".
 * You can use `.then()` or `await _wait(...` if you're running in async function
 * @param timeout Time to wait, in milliseconds
 */
export function waitFor(timeout) {
    return __awaiter(this, void 0, void 0, function* () {
        return new Promise((resolve, _) => setTimeout(() => resolve(), timeout));
    });
}
/**
 * Whether or not element has overflowing content vertically, horizontally, or both
 * Returns: true or false
 * @param element HTMLELement
 */
export function hasOverflowingContent(element) {
    if (!element) {
        return false;
    }
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
}
export function jsonpFetchWithTimeout(url, options, timeout = 5000) {
    return new Promise(function (resolve, reject) {
        const win = typeof window !== 'undefined' ? window : {};
        const callbackFnName = (options && options['jsonpCallback'])
            ? options.jsonpCallback
            : 'jsonpRequest_' + Math.random().toString(36).substr(2, 9);
        const callback = (options && options['callback'])
            ? options.callback
            : 'callback';
        // generate url
        let requestUrl = url;
        requestUrl += url.indexOf('?') > 0 ? '&' : '?';
        requestUrl += callback + '=' + callbackFnName;
        // append "data" items to URL
        if (options && options.data != null && typeof (options.data) === 'object') {
            for (const field in options.data) {
                requestUrl += '&' + field + '=' + options.data[field];
            }
        }
        // cache bust
        requestUrl += '&_=' + Math.random().toString(36).substr(2, 9);
        // create script tag
        const head = document.getElementsByTagName('head')[0] || document.head;
        const script = document.createElement('script');
        // setup the callback
        if (win[callbackFnName]) {
            reject(new Error('JSONP callback name already used'));
            script.parentNode.removeChild(script);
        }
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        win[callbackFnName] = function (res) {
            resolve(res);
            cleanUpScript('ok');
        };
        // NOTE: This actually causes things to break... so we're keeping it commented out.
        // make sure script tag can include credentials
        // if (options && options.hasOwnProperty('credentials') && options.credentials === 'include') {
        //     script.setAttribute('crossorigin', 'use-credentials');
        // }
        // listen for script error
        script.onerror = function () {
            cleanUpScript('Error trying to load script');
        };
        script.src = requestUrl;
        // start the timeout
        const tid = setTimeout(function () {
            cleanUpScript('Timeout');
        }, timeout);
        // append the script to get things rolling
        head.appendChild(script);
        function cleanUpScript(msg) {
            try {
                try {
                    delete win[callbackFnName];
                }
                catch (e) {
                    win[callbackFnName] = undefined;
                }
                script.parentNode.removeChild(script);
                clearTimeout(tid);
            }
            catch (e) {
                // nothing to do
            }
            if (msg === 'ok') {
                return;
            }
            msg = msg || '';
            reject(new Error('Fetch jsonp request to ' + url + ' failed!' + msg));
        }
    });
}
/**
 * Keep track of loading component library global style so that it is only done
 * once no matter how many components from the library are loaded on the page.
 */
export const TICOM_COMPONENTS_GLOBAL_STYLE_LOADED = {};
/**
 * Loads the global style sheet for a web component library by attaching
 * a link element with an href url based on the library's asset path.
 *
 * Will only load the style once no matter how many times this is called.
 */
export function loadGlobalStyle(libraryName) {
    if (!TICOM_COMPONENTS_GLOBAL_STYLE_LOADED[libraryName]) {
        // set the loaded flag first to avoid duplication
        TICOM_COMPONENTS_GLOBAL_STYLE_LOADED[libraryName] = true;
        // create the url to point to the css file
        const styleUrl = getAssetPath(`${libraryName}.css`);
        // create a new link element for the css file and attach it to the document head
        const linkEl = document.createElement('link');
        linkEl.rel = 'stylesheet';
        linkEl.type = 'text/css';
        linkEl.href = styleUrl;
        document.head.appendChild(linkEl);
    }
}
